import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  IonButton,
  IonPage,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonCard,
  IonCardContent,
  IonIcon,
  IonGrid,
  IonCol,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonSpinner,
} from '@ionic/react';
import { car, hammer, arrowDropleft, arrowDropright, checkmark, close, hand } from 'ionicons/icons';
import { ApiContext } from '../contexts/ApiContext';
import { getAllJobFlags, getPlan, getAllJobs } from '../utilities/Storage';
import { storePlans } from '../utilities/ApiTasks';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import {Toolbar} from "./Toolbar";

export default function Jobs() {
  const history = useHistory();

  const { planUpdated } = useContext(ApiContext);

  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [plan, setPlan] = useState();
  const [planDate, setPlanDate] = useState();
  const [jobs, setJobs] = useState();
  const [jobFlags, setJobFlags] = useState([]);

  const refresh = useCallback(async (getData = false) => {
    if (getData) {
      await storePlans(date);
    }

    const jf = await getAllJobFlags();
    const plan = await getPlan(date);
    const jobs = await getAllJobs();

    setJobFlags(jf);
    setPlan(JSON.parse(plan));
    setJobs(jobs);
  }, [date]);

  useEffect(() => {
    refresh();

    const unlisten = history.listen(h => {
      if (h.pathname === '/jobs') {
        getAllJobFlags().then(jf => setJobFlags(jf));
      }
    });

    return unlisten;
  }, []);

  useEffect(() => {
    let ignore = false;

    const getData = async (d) => {
      await storePlans(d);
      const plan = await getPlan(d);
      const jobs = await getAllJobs();

      if (!ignore) {
        setPlan(JSON.parse(plan));
        setPlanDate(d);
        setJobs(jobs);
      }
    }

    getData(date);

    return () => { ignore = true; }
  }, [date]);

  useEffect(() => {
    if (planUpdated) {
      refresh();
    }
  }, [planUpdated]);

  const toggleDate = interval => {
    setDate(moment(date).add(interval, 'days').format('YYYY-MM-DD'));
  }

  return (
    <IonPage>
      <IonHeader>
        <Toolbar title="Jobs" />
      </IonHeader>
      <IonContent padding>
        <IonRefresher slot="fixed" onIonRefresh={event => {
          refresh(true).then(() => event.detail.complete())
        }}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <IonGrid>
          <IonRow>
            <IonCol>
              <IonButton expand="full" color="medium" onClick={() => toggleDate(-1)}>
                <IonIcon icon={arrowDropleft} />
              </IonButton>
            </IonCol>
            <IonCol>
              <IonButton expand="full" color="medium"><strong>{moment(date).format('DD/MM/YY')}</strong></IonButton>
            </IonCol>
            <IonCol>
              <IonButton expand="full" color="medium" onClick={() => toggleDate(1)}>
                <IonIcon icon={arrowDropright} />
              </IonButton>
            </IonCol>
          </IonRow>
        </IonGrid>
        {planDate === date &&
        <React.Fragment>
          {plan && plan.plan && jobs
            ? <DayPlanPlan plan={plan} jobFlags={jobFlags} jobs={jobs} history={history} />
            : <h3 key="nojobs" className="ion-text-center">No data available.</h3>
          }
        </React.Fragment>
        }
        {planDate !== date &&
        <div className="ion-text-center">
          <IonSpinner color="teal" />
        </div>
        }
      </IonContent>
      <Redirector history={history} />
    </IonPage>
  );
}

function Redirector({history}) {
  const { redirector } = useContext(ApiContext);
  
  useEffect(() => {
    if (redirector && redirector.length > 0) {
      history.push(redirector);
    }
  }, [redirector]);

  return null;
}

const DayPlanPlan = React.memo(function DayPlanPlan({plan, jobFlags, jobs, history}) {

  let rows = [];
  let totalJobDuration = 0;
  let totalDistance = 0;
  let totalTravelTime = 0;
  let properties = {};

  if (plan.plan.length === 0) {
    rows.push(
      <h3 key="nojobs" className="ion-text-center">No jobs found for selected day.</h3>
    );
  }

  plan.plan.forEach((item, i) => {
    switch (item.type) {
      case 'travel':
        totalTravelTime += item.durationValue || 0;
        totalDistance += item.distanceValue || 0;

        rows.push(
          <IonItem color="light" button={true} key={'travel' + i}>
            <IonIcon icon={car} size="large" style={{marginRight: "5px"}} />        
            <IonLabel>
              <h3><strong>depart:</strong> {item.origin}</h3>
              <h3><strong>arrive:</strong> {item.destination}</h3>
              <h3><strong>time:</strong> {item.duration}, <strong>distance:</strong> {item.distance}</h3>
            </IonLabel>
          </IonItem>
        );
        break;
      case 'job':
          const job = plan.jobs[item.id];
          const property = job.property;

          if (!properties[property.id]) {
            properties[property.id] = property;
          }

          totalJobDuration += job.durationminutes;

          const start = job.preferredstartdatetime ? moment(job.preferredstartdatetime).format('HH:mm') : 'N/A';
          const duration = job.durationminutes ? job.durationminutes + ' minutes' : undefined;

          let departAt = undefined;
          if (plan.plan[i + 1].type === 'travel' && (i + 1) !== plan.plan.length - 1) {
            const nextJob = plan.jobs[plan.plan[i + 2].id];
            if (nextJob.preferredstartdatetime) {
              let nextJobStart = moment(nextJob.preferredstartdatetime);
              nextJobStart.subtract(plan.plan[i + 1].durationValue / 60, 'minutes');
              departAt = nextJobStart.format('HH:mm');
            }
          }

          let props;
          if (typeof jobFlags === 'object' && job.id in jobFlags) {
            props = jobFlags[job.id].value;
          }

          let fullJob;
          if (typeof jobs === 'object' && job.id in jobs) {
            fullJob = jobs[job.id].value;
          }

          rows.push(
            <IonItem 
              key={'job' + i} 
              button={true} 
              onClick={() => history.push(`/job/${job.id}`)} 
              detail
            >
              <IonIcon icon={hammer} size="large" style={{marginRight: "5px"}} />
              {job.association &&
              <IonIcon icon={hand} size="large" style={{marginRight: "5px"}} />
              }
              <IonLabel>
                <h3>{start} - {job.property.name}</h3>
                <p>{job.shortdescription}</p>
                <p>
                  <strong>
                    {duration ? duration + '.' : ''}
                    {departAt ? ' Depart by ' + departAt : ''}
                  </strong>
                </p>
              </IonLabel>
              {((props && props.completed) || (fullJob && fullJob.completeddate)) &&
              <IonIcon slot="end" icon={checkmark} size="large" />
              }
              {((props && props.cannotDoJob) || (fullJob && fullJob.substatus.workordersubstatus && fullJob.substatus.workordersubstatus.substatusreference === 'could_not_complete')) &&
              <IonIcon slot="end" icon={close} size="large" />
              }
            </IonItem>
          );
        break;
      default:
        break;
    }
  });

  return (
    <React.Fragment>
      {plan.length > 0 &&
      <IonGrid>
        <IonRow>        
          <IonCol>
            <IonCard className="ion-text-center ion-no-margin">
              <IonCardContent>
                <IonIcon icon={hammer} size="large" /><br />
                job duration<br />
                {totalJobDuration}
              </IonCardContent>
            </IonCard>
          </IonCol>
          <IonCol>
            <IonCard className="ion-text-center ion-no-margin">
              <IonCardContent>
                <IonIcon icon={car} size="large" /><br />
                travel time<br />
                {totalTravelTime}
              </IonCardContent>
            </IonCard>
          </IonCol>          
        </IonRow>
      </IonGrid>       
      }
      <IonList lines="full">
        {rows}
      </IonList>
    </React.Fragment>
  );

});
